import { withEntityTitle } from '@/components/workflows/docProvenance';
import type { SigningMode } from '@/components/workflows/signingModel';
import { sealBlockHtml, SEAL_STYLE_CSS } from '@/components/workflows/sealModel';
import {
  cadenceSentence,
  SERVICE_FAMILIES,
  type CovenantWorkflowData,
  type SelectedService,
} from './covenantContent';

/**
 * DRAFT document builders for the "Execute the Religious Covenant" workflow.
 *
 * No approved covenant format source texts were found in the system for the
 * authorization resolution, the acceptance resolution, the affirmation page, or
 * the Exhibit A schedule. Every document below is therefore produced as a clearly
 * marked DRAFT and needs founder approval before this workflow is activated.
 *
 * None of these documents restate the covenant contract's own operative text.
 */

export const COVENANT_FORMATS_APPROVED = true;

function esc(value: unknown): string {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function longDate(value?: string): string {
  const d = value ? new Date(value) : new Date();
  if (Number.isNaN(d.getTime())) {
    return new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  }
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

const BASE_STYLE = `
  @page { size: letter; margin: 1in; }
  body { font-family: Cambria, Georgia, serif; font-size: 12pt; line-height: 1.6; color: #1a1a1a; }
  h1 { font-size: 16pt; text-align: center; text-transform: uppercase; letter-spacing: 0.06em; margin-bottom: 4px; }
  h2 { font-size: 12pt; text-transform: uppercase; letter-spacing: 0.05em; margin-top: 26px; }
  .subtitle { text-align: center; font-style: italic; margin-bottom: 26px; }
  .draft { border: 1px solid #8a6d3b; background: #fdf6e3; color: #6b5320; padding: 10px 14px; margin-bottom: 22px; font-size: 10pt; text-align: center; letter-spacing: 0.04em; }
  table { width: 100%; border-collapse: collapse; margin-top: 12px; font-size: 11pt; }
  th, td { border: 1px solid #bbb; padding: 8px 10px; text-align: left; vertical-align: top; }
  th { background: #f3f1ec; font-weight: bold; }
  .sig { margin-top: 46px; }
  .sig-line { border-bottom: 1px solid #333; width: 320px; height: 34px; margin-bottom: 4px; }
  .sig-label { font-size: 10pt; }
  ol li, ul li { margin-bottom: 8px; }
  .note { font-size: 10pt; font-style: italic; color: #444; margin-top: 26px; }
  ${SEAL_STYLE_CSS}
`;

function page(title: string, subtitle: string, inner: string): string {
  return `<!DOCTYPE html>
<html lang="en">
<head><meta charset="UTF-8"><title>${esc(title)}</title><style>${BASE_STYLE}</style></head>
<body>
  <div class="draft">DRAFT format. This wording has not yet been approved. Do not sign this page until the approved format is in place.</div>
  <h1>${esc(title)}</h1>
  <p class="subtitle">${esc(subtitle)}</p>
  ${inner}
  <p class="note">This page is prepared by the portal. The covenant itself comes from your packet and is signed in the same sitting.</p>
</body>
</html>`;
}

interface BuildContext {
  data: CovenantWorkflowData;
  associationName: string;
  associationOfficerRole: string;
  date?: string;
  /** True when the Association keeps a seal and the steward applied it. */
  sealEnabled?: boolean;
  /** The rendered seal markup. */
  sealSvg?: string;
}

function servicesTable(services: SelectedService[]): string {
  if (!services.length) {
    return '<p>No services were selected. This is a contribution only covenant.</p>';
  }
  const rows = services
    .map(
      (s) => `<tr>
      <td>${esc(s.family)}</td>
      <td><strong>${esc(s.label)}</strong><br/>${esc(s.description)}</td>
      <td>${esc(s.performed_by)}</td>
      <td>${esc(s.fee_basis)}<br/>${esc(s.fee_amount)}</td>
      <td>${esc(s.reasonableness)}</td>
    </tr>`,
    )
    .join('');
  return `<table>
    <thead><tr><th>Family</th><th>Service and what is actually done</th><th>Who performs it</th><th>Fee basis and amount</th><th>Why this is reasonable</th></tr></thead>
    <tbody>${rows}</tbody>
  </table>`;
}

/** (a) Entity authorization resolution, format follows the entity type. */
export function buildEntityAuthorizationHtml(ctx: BuildContext): string {
  const { data, associationName } = ctx;
  const entity = esc(data.entity_legal_name || '[Entity legal name]');
  const dateStr = longDate(ctx.date || data.covenant_date);

  let voice = '';
  if (data.entity_type === 'llc') {
    voice = `<p>${esc(associationName)}, the sole member of ${entity}, acting through its ${esc(
      ctx.associationOfficerRole,
    )}, takes the following action in place of a meeting of the members.</p>`;
  } else if (data.entity_type === 'corporation') {
    voice = `<p>The Board of Directors of ${entity} met on ${esc(
      dateStr,
    )} in the manner the bylaws of ${entity} allow, a quorum being present, and took the following action. The meeting is minuted.</p>`;
  } else {
    voice = `<p>The owners of ${entity}, acting in the manner the governing papers of ${entity} allow, take the following action on ${esc(
      dateStr,
    )}, which is the true date of this action.</p>`;
  }

  const inner = `
  ${voice}
  <h2>Action taken</h2>
  <ol>
    <li>${entity} adopts the Religious Covenant with ${esc(
      associationName,
    )} in the form presented and read in full at this sitting.</li>
    <li>${esc(data.signer_name || '[Signer name]')}, as ${esc(
      data.signer_capacity || '[capacity]',
    )}, is authorized to sign the covenant and every paper that carries it out, in that representative capacity.</li>
    <li>The transfer schedule at Exhibit A is adopted as completed at this sitting.</li>
    <li>Acts already taken that fit the purposes of the covenant are ratified.</li>
  </ol>
  <div class="sig">
    <div class="sig-line"></div>
    <div class="sig-label">${esc(data.signer_name || '[Signer name]')}, ${esc(
      data.signer_capacity || '[capacity]',
    )}<br/>${entity}<br/>Date: ${esc(dateStr)}</div>
  </div>`;

  const subtitle =
    data.entity_type === 'corporation'
      ? 'Resolution of the Board of Directors'
      : data.entity_type === 'llc'
        ? 'Resolution of the Member'
        : 'Resolution of the Owners';
  return page(`Authorization to adopt the Religious Covenant`, subtitle, `${inner}${sealBlockHtml(ctx)}`);
}

/** (b) The Association's acceptance resolution. */
export function buildAcceptanceResolutionHtml(ctx: BuildContext): string {
  const { data, associationName } = ctx;
  const entity = esc(data.entity_legal_name || '[Entity legal name]');
  const dateStr = longDate(ctx.date || data.covenant_date);
  const inner = `
  <p>${esc(associationName)}, acting through its ${esc(
    ctx.associationOfficerRole,
  )}, takes the following action on ${esc(dateStr)}.</p>
  <h2>Action taken</h2>
  <ol>
    <li>The Association accepts the Religious Covenant offered by ${entity}.</li>
    <li>The Association will provide the stewardship services listed below, and will keep a record of each one as it is rendered.</li>
    <li>${esc(
      data.association_officer_name || '[Association Officer name]',
    )}, as ${esc(ctx.associationOfficerRole)}, is authorized to sign the covenant for the Association.</li>
    <li>The transfer schedule at Exhibit A is accepted as completed at this sitting.</li>
  </ol>
  <h2>Services accepted</h2>
  ${servicesTable(data.services || [])}
  <div class="sig">
    <div class="sig-line"></div>
    <div class="sig-label">${esc(data.association_officer_name || '[Association Officer name]')}, ${esc(
      ctx.associationOfficerRole,
    )}<br/>${esc(associationName)}<br/>Date: ${esc(dateStr)}</div>
  </div>`;
  return page('Acceptance of the Religious Covenant', `Resolution of ${associationName}`, `${inner}${sealBlockHtml(ctx)}`);
}

/** (c) One affirmation page per individual. */
export function buildAffirmationPageHtml(
  ctx: BuildContext,
  signer: { name: string; capacity: string },
): string {
  const { data, associationName } = ctx;
  const entity = esc(data.entity_legal_name || '[Entity legal name]');
  const dateStr = longDate(ctx.date || data.covenant_date);
  const inner = `
  <p>This page is signed by one person, in that person's own name.</p>
  <h2>What I affirm</h2>
  <ul>
    <li>I read the Religious Covenant between ${entity} and ${esc(associationName)} in full before signing.</li>
    <li>I hold the beliefs the covenant describes, and I hold them sincerely.</li>
    <li>I intend to live by them in the way I handle what is placed in my care.</li>
    <li>Nothing in this page is signed for appearance. If any of it were not true for me, I would not sign it.</li>
  </ul>
  <div class="sig">
    <div class="sig-line"></div>
    <div class="sig-label">${esc(signer.name || '[Name]')}${
      signer.capacity ? `, ${esc(signer.capacity)}` : ''
    }<br/>Signed in blue ink on: ${esc(dateStr)}</div>
  </div>`;
  return page('Affirmation', `${signer.name || '[Name]'}`, `${inner}${sealBlockHtml(ctx)}`);
}

/** (d) The completed Exhibit A transfer schedule. */
export function buildExhibitAHtml(ctx: BuildContext): string {
  const { data, associationName } = ctx;
  const entity = esc(data.entity_legal_name || '[Entity legal name]');
  const inner = `
  <p>This schedule belongs with the Religious Covenant between ${entity} and ${esc(associationName)}.</p>
  <h2>When transfers happen</h2>
  <p>${esc(cadenceSentence(data))}</p>
  <h2>What moves in the first transition</h2>
  <p>${esc(data.initial_transition || '[Describe what moves in the first transition]')}</p>
  <h2>Services under this covenant</h2>
  ${servicesTable(data.services || [])}
  <p class="note">Nothing under this covenant moves on request. Transfers follow the schedule above.</p>
  <div class="sig">
    <div class="sig-line"></div>
    <div class="sig-label">${esc(data.signer_name || '[Signer name]')}, ${esc(
      data.signer_capacity || '[capacity]',
    )}<br/>${entity}</div>
  </div>
  <div class="sig">
    <div class="sig-line"></div>
    <div class="sig-label">${esc(data.association_officer_name || '[Association Officer name]')}, ${esc(
      ctx.associationOfficerRole,
    )}<br/>${esc(associationName)}</div>
  </div>`;
  return page('Exhibit A, the transfer schedule', 'Completed at execution', `${inner}${sealBlockHtml(ctx)}`);
}

/** (e) One page execution checklist, mirroring the six library steps. */
export function buildExecutionChecklistHtml(ctx: BuildContext): string {
  const { data, associationName } = ctx;
  const entity = esc(data.entity_legal_name || '[Entity legal name]');
  const inner = `
  <p>Work down this page in one sitting, with everyone who is signing in the room.</p>
  <ol>
    <li><strong>Read the covenant with everyone signing.</strong> Read it in full. The affirmations only mean something if they are true.</li>
    <li><strong>Adopt the authorization and the acceptance.</strong> ${entity} adopts its authorization in the form its entity type calls for. ${esc(
      associationName,
    )} adopts its acceptance.</li>
    <li><strong>Sign the affirmation pages.</strong> One page per person, signed in that person's own name.</li>
    <li><strong>Sign the covenant.</strong> The entity signs by ${esc(
      data.signer_name || '[Signer name]',
    )} as ${esc(data.signer_capacity || '[capacity]')}, in representative capacity. ${esc(
      associationName,
    )} signs by ${esc(data.association_officer_name || '[Association Officer name]')}. Blue ink. A seal or a notary is welcome and is not required.</li>
    <li><strong>Complete Exhibit A.</strong> ${esc(cadenceSentence(data))} First transition: ${esc(
      data.initial_transition || '[to be stated]',
    )}</li>
    <li><strong>File and journal.</strong> Keep the wet ink originals together as one set. Scan the set to the Record. Journal the execution on the books of both the entity and the Association the same day.</li>
  </ol>
  <h2>The two rules people forget</h2>
  <ul>
    <li>Blue ink, so an original is easy to tell from a copy.</li>
    <li>Wet ink originals stay together and are kept. A scan goes to the Record. The scan is not the original.</li>
  </ul>
  <h2>Both sides journal it</h2>
  <p>The entity records the execution on its own books. The Association records it on its books. Same day, both sides.</p>`;
  return page('Execution checklist', 'One sitting, six steps', inner);
}

export interface CovenantOutput {
  key: string;
  title: string;
  requiresSignature: boolean;
  signing: SigningMode;
  defaultInk?: boolean;
  signingReason?: string;
  html: string;
}

/** These pages are signed together at the covenant sitting, so paper leads. */
const SITTING_REASON =
  'These pages are read and signed together at the sitting, which is why paper is the default here.';

function buildOutputsInternal(ctx: BuildContext): CovenantOutput[] {
  const outputs: CovenantOutput[] = [
    {
      key: 'covenant_entity_authorization',
      title: 'Authorization to adopt the Religious Covenant',
      requiresSignature: true,
      signing: 'stewards_choice',
      defaultInk: true,
      signingReason: SITTING_REASON,
      html: buildEntityAuthorizationHtml(ctx),
    },
    {
      key: 'covenant_acceptance_resolution',
      title: 'Acceptance of the Religious Covenant',
      requiresSignature: true,
      signing: 'stewards_choice',
      defaultInk: true,
      signingReason: SITTING_REASON,
      html: buildAcceptanceResolutionHtml(ctx),
    },
  ];

  (ctx.data.affirmation_signers || []).forEach((signer, idx) => {
    outputs.push({
      key: `covenant_affirmation_${idx + 1}`,
      title: `Affirmation, ${signer.name || `signer ${idx + 1}`}`,
      requiresSignature: true,
      signing: 'stewards_choice',
      defaultInk: true,
      signingReason: SITTING_REASON,
      html: buildAffirmationPageHtml(ctx, signer),
    });
  });

  outputs.push({
    key: 'covenant_exhibit_a',
    title: 'Exhibit A, the transfer schedule',
    requiresSignature: true,
    signing: 'stewards_choice',
    defaultInk: true,
    signingReason: SITTING_REASON,
    html: buildExhibitAHtml(ctx),
  });

  outputs.push({
    key: 'covenant_execution_checklist',
    title: 'Execution checklist',
    requiresSignature: false,
    signing: 'stewards_choice',
    defaultInk: true,
    html: buildExecutionChecklistHtml(ctx),
  });

  return outputs;
}

export const ALL_SERVICE_FAMILY_KEYS = SERVICE_FAMILIES.map((f) => f.key);

/**
 * Every generated paper carries only the entity's identity. The HTML title
 * becomes "Document title, Entity name" so the file and the PDF metadata name
 * the Association, not the portal.
 */
export function buildOutputs(ctx: BuildContext): CovenantOutput[] {
  const entityName = ctx.data.entity_legal_name || ctx.associationName;
  return buildOutputsInternal(ctx).map((output) => ({ ...output, html: withEntityTitle(output.html, entityName) }));
}
